import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Require2FA = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const check2FAStatusRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous calls
    if (check2FAStatusRef.current) return;
    
    check2FAStatusRef.current = true;
    check2FAStatus().finally(() => {
      // Reset after a delay to allow for React Strict Mode double calls
      setTimeout(() => {
        check2FAStatusRef.current = false;
      }, 1000);
    });
  }, []);

  const check2FAStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/auth/me`, getAuthHeaders());
      
      if (response.data.twoFactorEnabled) {
        // Already enabled, redirect to dashboard
        navigate('/dashboard');
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to check 2FA status:', err);
      // Handle rate limiting (429)
      if (err.response && err.response.status === 429) {
        // Wait a bit and retry once
        setTimeout(async () => {
          try {
            const retryResponse = await axios.get(`${API_BASE_URL}/auth/me`, getAuthHeaders());
            if (retryResponse.data.twoFactorEnabled) {
              navigate('/dashboard');
            } else {
              setLoading(false);
            }
          } catch (retryErr) {
            setError('Too many requests. Please wait a moment and refresh the page.');
            setLoading(false);
          }
        }, 2000);
        return;
      }
      // If token is expired or invalid (401/403), redirect to login
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('adminToken');
        navigate('/login', { replace: true });
        return;
      }
      setError('Failed to load user information');
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setSettingUp(true);
      setError('');
      const response = await axios.post(`${API_BASE_URL}/auth/setup-2fa`, {}, getAuthHeaders());
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setSetupDialogOpen(true);
    } catch (err) {
      // If token is expired or invalid (401/403), redirect to login
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('adminToken');
        navigate('/login', { replace: true });
        return;
      }
      setError(err.response?.data?.message || 'Failed to setup 2FA');
    } finally {
      setSettingUp(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setEnabling(true);
      setError('');
      await axios.post(
        `${API_BASE_URL}/auth/enable-2fa`,
        { secret, token: verificationCode },
        getAuthHeaders()
      );
      
      // 2FA enabled successfully, redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      // If token is expired or invalid (401/403), redirect to login
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.removeItem('adminToken');
        navigate('/login', { replace: true });
        return;
      }
      setError(err.response?.data?.message || 'Failed to enable 2FA');
    } finally {
      setEnabling(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#f5f6fa'
      }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#f5f6fa',
      padding: '1.5rem'
    }}>
      <Card style={{ 
        width: '100%', 
        maxWidth: '500px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <i className="pi pi-shield" style={{ 
              fontSize: '4rem', 
              color: '#667eea',
              marginBottom: '1rem'
            }}></i>
            <h2 style={{ 
              margin: '0 0 0.5rem 0',
              fontSize: '1.75rem',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              Two-Factor Authentication Required
            </h2>
            <p style={{ 
              margin: 0,
              color: '#6c757d',
              fontSize: '0.95rem'
            }}>
              You must enable 2FA to access the admin dashboard
            </p>
          </div>

          {error && (
            <Message 
              severity="error" 
              text={error} 
              style={{ marginBottom: '1.5rem' }} 
              onClose={() => setError('')} 
            />
          )}

          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem', lineHeight: '1.6' }}>
              <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
              <strong>Security Requirement:</strong> For your account security, you must enable Two-Factor Authentication (2FA) before you can access the admin dashboard.
            </p>
          </div>

          <Button
            label="Setup 2FA"
            icon="pi pi-shield"
            iconPos="left"
            onClick={handleSetup2FA}
            disabled={settingUp || enabling}
            loading={settingUp}
            className="p-button-lg"
            style={{ 
              width: '100%',
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: '500',
              marginTop: '1rem'
            }}
          />
        </div>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog
        header="Setup Two-Factor Authentication"
        visible={setupDialogOpen}
        style={{ width: '90vw', maxWidth: '500px' }}
        onHide={() => {
          if (!enabling) {
            setSetupDialogOpen(false);
            setVerificationCode('');
          }
        }}
        closable={!enabling}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setSetupDialogOpen(false);
                setVerificationCode('');
              }}
              className="p-button-text p-button-lg"
              disabled={enabling}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem'
              }}
            />
            <Button
              label="Enable 2FA"
              icon={enabling ? null : "pi pi-check"}
              iconPos="left"
              onClick={handleEnable2FA}
              disabled={enabling || !verificationCode || verificationCode.length !== 6}
              loading={enabling}
              className="p-button-lg"
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '500',
                minWidth: '140px'
              }}
            />
          </div>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ marginBottom: '1.5rem', color: '#495057', lineHeight: '1.6' }}>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):
          </p>

          {qrCode && (
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <img 
                src={qrCode} 
                alt="QR Code" 
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  backgroundColor: '#fff'
                }} 
              />
            </div>
          )}

          <div style={{ 
            background: '#e7f3ff', 
            border: '1px solid #b3d9ff', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#0056b3', fontSize: '0.9rem' }}>
              Manual Entry (if QR code doesn't work):
            </p>
            <code style={{ 
              display: 'block',
              padding: '0.5rem',
              background: '#fff',
              borderRadius: '4px',
              fontSize: '0.85rem',
              wordBreak: 'break-all',
              color: '#495057'
            }}>
              {secret}
            </code>
          </div>

          <div className="p-field" style={{ marginBottom: '1.5rem' }}>
            <label 
              htmlFor="verificationCode" 
              className="p-d-block" 
              style={{ 
                marginBottom: '0.75rem', 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem'
              }}
            >
              Enter 6-digit code from your authenticator app *
            </label>
            <InputText
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
                if (error) setError('');
              }}
              style={{ 
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.25rem',
                letterSpacing: '0.5rem',
                textAlign: 'center',
                fontFamily: 'monospace'
              }}
              placeholder="000000"
              maxLength={6}
              disabled={enabling}
              autoFocus
            />
          </div>

          {error && (
            <Message 
              severity="error" 
              text={error} 
              style={{ marginBottom: '1rem' }} 
              onClose={() => setError('')} 
            />
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default Require2FA;





